/**
 * components/email/DeliverabilityCard.tsx — collapsed-by-default card on
 * the EmailDashboard SubCard. GET /deliverability → score, reasons, DNSBL
 * listings, rDNS, record ticks. "Re-check" re-fetches; a 429 is shown as
 * "try again in a few minutes".
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Deliverability, fetchDeliverability, fmtRelative, friendlyError } from './emailApi';

const scoreChip = (score: Deliverability['score'] | null) => {
  switch (score) {
    case 'good':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-500 text-primary-navy font-semibold">✓ Good</span>;
    case 'attention':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-100 border border-secondary-500 text-primary-navy">⚠ Needs attention</span>;
    case 'bad':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary-navy text-white font-semibold">✕ Problems</span>;
    default:
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">· Not checked</span>;
  }
};

const Tick: React.FC<{ ok: boolean | 'unknown'; label: string }> = ({ ok, label }) => (
  <li className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${ok === true ? 'bg-secondary-500 border-secondary-500 text-primary-navy font-semibold' : ok === 'unknown' ? 'bg-gray-50 border-gray-200 text-gray-500' : 'bg-white border-gray-300 text-primary-navy'}`}>
    <span aria-hidden>{ok === true ? '✓' : ok === 'unknown' ? '?' : '○'}</span>
    {label}
    <span className="sr-only">: {ok === true ? 'ok' : ok === 'unknown' ? 'unknown' : 'missing'}</span>
  </li>
);

export const DeliverabilityCard: React.FC<{ domainRowId: string; defaultOpen?: boolean }> = ({ domainRowId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState<Deliverability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchDeliverability(domainRowId);
      setData(d);
      setError(null);
      setCheckedAt(new Date());
    } catch (e: any) {
      if (e?.status === 429) setError('Checked recently — try again in a few minutes.');
      else setError(friendlyError(e, 'Couldn\'t run the deliverability check'));
    } finally { setLoading(false); setLoadedOnce(true); }
  }, [domainRowId]);

  // Fetch lazily on first expand so the dashboard list stays light.
  useEffect(() => { if (open && !loadedOnce) load(); }, [open, loadedOnce, load]);

  const panelId = `deliverability-${domainRowId}`;
  const listedCount = (data?.dnsbl || []).filter(d => d.listed === true).length;

  return (
    <div className="border border-gray-200 rounded-xl bg-white">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-primary-navy">
          <span aria-hidden className={`inline-block mr-1 transition-transform ${open ? 'rotate-90' : ''}`}>▸</span> Deliverability
        </span>
        <span className="flex items-center gap-2">
          {scoreChip(data?.score ?? null)}
        </span>
      </button>
      {open && (
        <div id={panelId} className="px-4 pb-4 space-y-3 text-sm">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-gray-500">
              {checkedAt ? `Checked ${fmtRelative(checkedAt.toISOString())}` : loading ? 'Checking…' : 'Not checked yet'}
            </span>
            <button type="button" onClick={load} disabled={loading} className="text-xs bg-white border border-gray-300 text-primary-navy px-3 py-1 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {loading ? 'Checking…' : 'Re-check'}
            </button>
          </div>

          {error && <div role="alert" className="text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {error}</div>}

          {data && (
            <>
              {data.reasons && data.reasons.length > 0 ? (
                <ul className="list-disc pl-5 space-y-0.5 text-primary-navy/90">
                  {data.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              ) : (
                <p className="text-primary-navy/80">No issues found. Mail from this domain should land in inboxes.</p>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">DNS records</div>
                <ul className="flex flex-wrap gap-1.5">
                  <Tick ok={!!data.records?.mx} label="MX" />
                  <Tick ok={!!data.records?.spf} label="SPF" />
                  <Tick ok={!!data.records?.dkim} label="DKIM" />
                  <Tick ok={!!data.records?.dmarc} label="DMARC" />
                  <Tick ok={!!data.rdns_ok} label="Reverse DNS" />
                </ul>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                  Blocklists (DNSBL){listedCount > 0 ? ` — ${listedCount} listing${listedCount === 1 ? '' : 's'}` : ''}
                </div>
                {data.dnsbl && data.dnsbl.length > 0 ? (
                  <ul className="flex flex-wrap gap-1.5">
                    {data.dnsbl.map(d => (
                      <li key={d.zone} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-mono ${d.listed === true ? 'bg-primary-navy border-primary-navy text-white' : d.listed === 'unknown' ? 'bg-gray-50 border-gray-200 text-gray-500' : 'bg-white border-gray-300 text-primary-navy'}`}>
                        <span aria-hidden>{d.listed === true ? '✕' : d.listed === 'unknown' ? '?' : '✓'}</span>
                        {d.zone}
                        <span className="font-sans">{d.listed === true ? 'listed' : d.listed === 'unknown' ? 'unknown' : 'clear'}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500">No blocklist data.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliverabilityCard;
